'use client';

import { useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

interface Founder {
    id: number;
    name: string;
    role: string;
    expertise: string[];
    image: string;
    alt: string;
    description: string;
    social: { icon: string; link: string }[];
}

const FOUNDERS: Founder[] = [
    {
        id: 1,
        name: 'Abdul Sohail Samad',
        role: 'Founder & Technical Lead',
        expertise: ['Full-Stack Arch.', 'Next.js Expert', 'Scalable Systems'],
        image: "/abdulsohailsamad.jpg",
        alt: 'Abdul Sohail Samad - Technical Lead',
        description: 'Architecting the digital backbone of modern enterprises. Expert in translating complex business logic into seamless, high-performance web applications.',
        social: [{ icon: 'CodeBracketIcon', link: '#' }, { icon: 'CpuChipIcon', link: '#' }]
    }
];

const FounderCard = ({ founder, index, isActive, onClick }: { founder: Founder; index: number; isActive: boolean; onClick: () => void }) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null); // Changed to DivElement to wrap AppImage

    // 3D Tilt Effect (Only active when NOT popped out)
    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!cardRef.current || isActive) return;

        const rect = cardRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        const rotateX = ((y - centerY) / centerY) * -10; // Max 10deg rotation
        const rotateY = ((x - centerX) / centerX) * 10;

        gsap.to(cardRef.current, {
            rotationX: rotateX,
            rotationY: rotateY,
            scale: 1.02,
            duration: 0.4,
            ease: "power2.out",
            transformPerspective: 1000
        });

        // Parallax Content
        gsap.to(contentRef.current, {
            x: rotateY * 1.5,
            y: rotateX * 1.5,
            duration: 0.4
        });
    };

    const handleMouseLeave = () => {
        if (isActive) return;

        gsap.to(cardRef.current, {
            rotationX: 0,
            rotationY: 0,
            scale: 1,
            duration: 0.7,
            ease: "elastic.out(1, 0.5)"
        });

        gsap.to(contentRef.current, {
            x: 0,
            y: 0,
            duration: 0.7
        });
    };

    // Handle Active State ("Pop Out") effect
    useGSAP(() => {
        if (isActive) {
            // Pop Out Animation
            gsap.to(cardRef.current, {
                rotationX: 0,
                rotationY: 0,
                scale: 1.1,
                zIndex: 50,
                duration: 0.5,
                ease: "back.out(1.7)"
            });

            // Image "Breaks Frame" effect
            gsap.to(imageRef.current, {
                scale: 1.15,
                y: -10,
                duration: 0.5
            });

            // Continuous Floating Hover
            gsap.to(cardRef.current, {
                y: -15,
                duration: 2,
                repeat: -1,
                yoyo: true,
                ease: "sine.inOut",
                delay: 0.5
            });
        } else {
            // Reset
            gsap.killTweensOf(cardRef.current); // Stop floating
            gsap.to(cardRef.current, {
                scale: 1,
                zIndex: 1,
                y: 0,
                rotationX: 0,
                rotationY: 0,
                duration: 0.5,
                ease: "power2.out"
            });
            gsap.to(imageRef.current, {
                scale: 1,
                y: 0,
                duration: 0.5
            });
        }
    }, { scope: cardRef, dependencies: [isActive] });


    return (
        <div
            ref={cardRef}
            onClick={onClick}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className={`group relative w-full aspect-[3/5] md:aspect-[4/5] rounded-3xl bg-white shadow-xl transition-all duration-300 border border-slate-100 will-change-transform cursor-pointer ${isActive ? 'overflow-visible ring-4 ring-indigo-400/30' : 'overflow-hidden'}`}
        >
            {/* Image Layer */}
            <div className="absolute inset-0 z-0 rounded-3xl overflow-hidden">
                <div ref={imageRef} className="w-full h-full relative">
                    <img
                        src={founder.image}
                        alt={founder.alt}
                        className="w-full h-full object-cover object-[center_15%] transition-transform duration-700 pointer-events-none select-none"
                    />

                </div>
            </div>

            {/* Glass Content Layer */}
            <div ref={contentRef} className="absolute inset-0 z-10 flex flex-col justify-end p-6 sm:p-8 transform-style-3d pointer-events-none">
                <div className={`transform transition-transform duration-500 ${isActive ? 'translate-y-0' : 'translate-y-4 group-hover:translate-y-0'}`}>
                    {/* Role Tag */}
                    <div className={`inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-3`}>
                        <Icon name={index === 0 ? "CodeBracketIcon" : "ChartBarIcon"} size={14} className="text-white" />
                        <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-white">{founder.role}</span>
                    </div>

                    <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">{founder.name}</h3>
                    <p className="text-slate-300 text-xs sm:text-sm leading-relaxed mb-4 max-w-[95%] sm:max-w-[90%]">
                        {founder.description}
                    </p>

                    {/* Expertise Pills */}
                    <div className="flex flex-wrap gap-2 mb-4 pointer-events-auto">
                        {founder.expertise.map((skill, i) => (
                            <span key={i} className="text-[10px] font-bold uppercase tracking-wide text-slate-900 bg-white px-3 py-1 rounded shadow-sm">
                                {skill}
                            </span>
                        ))}
                    </div>

                    {/* Social/Action */}
                    <div className="flex gap-4 pt-4 border-t border-white/10 pointer-events-auto">
                        {founder.social.map((s, i) => (
                            <button key={i} className="p-2 rounded-full bg-white/5 hover:bg-white text-white hover:text-slate-900 transition-colors">
                                <Icon name={s.icon as any} size={18} />
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

import MobileFoundersSection from './MobileFoundersSection';

const FoundersSection = () => {
    const containerRef = useRef<HTMLElement>(null);
    const [activeId, setActiveId] = useState<number | null>(null);

    useGSAP(() => {
        // Only run desktop animations if screen is large enough
        // Basic check, usually better with matchMedia but GSAP handles queries too
        const mm = gsap.matchMedia();

        mm.add("(min-width: 768px)", () => {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: "top 70%",
                    toggleActions: "play none none reverse"
                }
            });

            // Header Reveal
            tl.from(".founders-header > *", {
                y: 50,
                opacity: 0,
                duration: 1,
                stagger: 0.1,
                ease: "power3.out"
            });

            // Connector Line Draw
            tl.from(".connector-line", {
                scaleX: 0,
                opacity: 0,
                duration: 1,
                ease: "expo.inOut"
            }, "-=0.5");

            // Cards Float Up
            tl.from(".founder-wrapper", {
                y: 100,
                opacity: 0,
                duration: 1.2,
                stagger: 0.2,
                ease: "power4.out"
            }, "-=0.8");
        });

    }, { scope: containerRef });

    return (
        <section ref={containerRef} className="bg-slate-50 relative overflow-hidden">
            {/* --- MOBILE VIEW --- */}
            <div className="block md:hidden">
                <MobileFoundersSection />
            </div>

            {/* --- DESKTOP VIEW --- */}
            <div className="hidden md:block py-32 relative">
                {/* Background Elements */}
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
                <div className="absolute -left-40 top-40 w-96 h-96 bg-blue-100/50 rounded-full blur-3xl mix-blend-multiply" />
                <div className="absolute -right-40 bottom-40 w-96 h-96 bg-indigo-100/50 rounded-full blur-3xl mix-blend-multiply" />

                <div className="container mx-auto px-6 relative z-10">
                    {/* Header */}
                    <div className="founders-header text-center max-w-3xl mx-auto mb-20">
                        <span className="inline-block text-sm font-bold text-indigo-600 tracking-[0.2em] uppercase mb-4">Leadership</span>
                        <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-6 tracking-tight">
                            Meet the <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-500">Founder</span>
                        </h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            Driven by a passion for elite technical architecture and aggressive market strategy.
                            We don't just build software; we engineer dominance.
                        </p>
                    </div>

                    {/* Cards Container */}
                    <div className="relative max-w-lg mx-auto">
                        <div className="relative z-10">
                            {FOUNDERS.map((founder, index) => (
                                <div key={founder.id} className="founder-wrapper perspective-1000">
                                    <FounderCard
                                        founder={founder}
                                        index={index}
                                        isActive={activeId === founder.id}
                                        onClick={() => setActiveId(activeId === founder.id ? null : founder.id)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default FoundersSection;